// システム管理者の登録・削除（portal.md「システム管理者」/ portal-api.md「システム管理者向け」）。system-admin のみ。
// system-admin は契約のユーザー（管理者・メンバー）と兼ねない。登録しても本人にメールは送らない（ADR-0028）。

import { useEffect, useState } from "react";
import { api, ApiError, messageFor, type Me, type SystemAdmin } from "../lib/api";
import { currentUser, signIn } from "../lib/auth";
import { EMAIL_RE } from "../lib/login";
import { SystemHeader } from "./Header";
import { SubCode, fmt } from "./shared";

export function SystemAdminsPage() {
  const [me, setMe] = useState<Me | null>(null);
  const [admins, setAdmins] = useState<SystemAdmin[] | null>(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = async () => setAdmins((await api.systemAdmins()).systemAdmins);
  useEffect(() => {
    (async () => {
      if (!(await currentUser())) { await signIn(); return; }
      try {
        const m = await api.me();
        setMe(m);
        if (m.platformRole === "system-admin") await load();
      } catch (e) { setError(messageFor(e)); }
    })();
  }, []);

  if (me && me.platformRole !== "system-admin") return <main><p className="error">{messageFor(new ApiError(403, "forbidden"))}</p></main>;
  if (!me || !admins) return <main>{error ? <p className="error">{error}</p> : <p className="muted">読み込み中…</p>}</main>;

  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    const v = email.trim().toLowerCase();
    if (!EMAIL_RE.test(v)) { setError("メールアドレスの形式が正しくありません。"); return; }
    setBusy(true); setError(null); setNotice(null);
    try { await api.addSystemAdmin(v); setNotice(`${v} をシステム管理者に登録しました。`); setEmail(""); await load(); }
    catch (err) { setError(messageFor(err)); } finally { setBusy(false); }
  };

  const remove = async (a: SystemAdmin) => {
    if (!confirm(`${a.email} をシステム管理者から削除します。共有プールのユーザーも削除され、ポータルにログインできなくなります。よろしいですか？`)) return;
    setError(null); setNotice(null);
    try { await api.removeSystemAdmin(a.sub); setNotice(`${a.email} を削除しました。`); await load(); }
    catch (err) { setError(messageFor(err)); }
  };

  return (
    <>
      <SystemHeader me={me} />
      <main>
        <h1>システム管理者</h1>
        <p className="muted">
          全契約の台帳を変更できます。契約のユーザー（管理者・メンバー）とは別の email で登録します
          （契約に所属している email は登録できず、システム管理者を契約のユーザーにすることもできません）。
        </p>

        <form onSubmit={add} className="row">
          <input type="email" placeholder="メールアドレス" value={email} onChange={(e) => setEmail(e.target.value)} aria-label="登録するメールアドレス" />
          <button className="primary" type="submit" disabled={busy}>{busy ? "処理中…" : "登録"}</button>
        </form>
        <p className="muted">
          登録しても本人にメールは届きません。SSO（会社の IdP）の email ならそのままログインできます。
          パスワード方式の email は仮パスワードが届かないため、登録してもログインできません。
        </p>
        {error && <p className="error">{error}</p>}
        {notice && <p className="ok">{notice}</p>}

        <table>
          <thead><tr><th>メールアドレス</th><th>Cognito sub</th><th>登録日</th><th>最終ログイン</th><th></th></tr></thead>
          <tbody>
            {admins.map((a) => (
              <tr key={a.sub}>
                <td>{a.email}{a.sub === me.sub && <span className="muted">（自分）</span>}</td>
                <td><SubCode sub={a.sub} /></td>
                <td className="muted">{fmt(a.createdAt)}</td>
                <td className="muted">{fmt(a.lastLoginAt)}</td>
                <td>
                  <button className="danger" disabled={a.sub === me.sub} title={a.sub === me.sub ? "自分自身は削除できません" : undefined}
                    onClick={() => void remove(a)}>削除</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </main>
    </>
  );
}
